import React, { useState, useEffect, useRef } from 'react';
import { DsButton, Eyebrow, ModalDialog, SegmentedControl } from '../ds';
import PermissionRow from './PermissionRow';

export interface ContainersPermissionSettings {
  General: 'Read'; // Only Read, no Write option
  BuildAndDeploy: 'None' | 'Read' | 'Write';
  EnvVars: 'None' | 'Read' | 'Write';
  Domain: 'None' | 'Read' | 'Write';
  HealthAndAlert: 'None' | 'Read' | 'Write';
}

export interface ContainersPermissionInput {
  Overview: 'Read'; // Only Read, no Write option
  Logs: 'Read'; // Only Read, no Write option
  Deployments: 'Read'; // Only Read, no Write option
  Metrics: 'Read'; // Only Read, no Write option
  Settings: ContainersPermissionSettings;
  Actions: {
    Deploy: 'Read' | 'Write';
  };
}

interface CollaboratorPermissionModalProps {
  open: boolean;
  email: string;
  onClose: () => void;
  onConfirm: (permissions: ContainersPermissionInput) => void;
  defaultPermissions?: ContainersPermissionInput;
  customPermissions?: ContainersPermissionInput;
  error?: string;
  isSubmitting?: boolean;
}

const defaultFeaturesPermissions: ContainersPermissionInput = {
  Overview: 'Read',
  Logs: 'Read',
  Deployments: 'Read',
  Metrics: 'Read',
  Settings: {
    General: 'Read',
    BuildAndDeploy: 'Write',
    EnvVars: 'Write',
    Domain: 'Write',
    HealthAndAlert: 'Write',
  },
  Actions: {
    Deploy: 'Write',
  },
};

const settingsFeatures = [
  { key: 'BuildAndDeploy', label: 'Build & Deploy', description: 'Configure build and deployment settings', readOnly: false },
  { key: 'EnvVars', label: 'Environment Variables', description: 'Manage environment variables', readOnly: false },
  { key: 'Domain', label: 'Domain', description: 'Manage custom domains', readOnly: false },
  { key: 'HealthAndAlert', label: 'Health and Alert', description: 'Configure health checks and alerts', readOnly: false },
];

const CollaboratorPermissionModal = ({
  open,
  email,
  onClose,
  onConfirm,
  defaultPermissions = defaultFeaturesPermissions,
  customPermissions,
  error,
  isSubmitting = false,
}: CollaboratorPermissionModalProps) => {
  const [isDefault, setIsDefault] = useState(!customPermissions);
  const [permissions, setPermissions] = useState<ContainersPermissionInput>(
    customPermissions || defaultPermissions
  );
  const prevOpenRef = useRef(open);
  const prevCustomPermissionsRef = useRef(customPermissions);

  // Reset permissions only when modal transitions from closed to open, or when customPermissions changes
  // Don't reset during submission or when modal is already open
  // Reset on error (when error appears after a failed request)
  useEffect(() => {
    const wasClosed = !prevOpenRef.current;
    const isNowOpen = open;
    const customPermissionsChanged = prevCustomPermissionsRef.current !== customPermissions;
    
    // Only reset if:
    // 1. Modal just opened (was closed, now open) AND not submitting
    // 2. OR customPermissions changed AND modal is open AND not submitting
    // 3. OR error appeared (request failed) - reset to original permissions
    const shouldReset = (wasClosed && isNowOpen && !isSubmitting) || 
                       (customPermissionsChanged && open && !isSubmitting) ||
                       (error && !isSubmitting && prevOpenRef.current === open);
    
    if (shouldReset) {
      setIsDefault(!customPermissions);
      setPermissions(customPermissions || defaultPermissions);
    }
    
    prevOpenRef.current = open;
    prevCustomPermissionsRef.current = customPermissions;
  }, [open, customPermissions, defaultPermissions, isSubmitting, error]);

  const handlePermissionChange = (
    section: 'main' | 'settings' | 'actions',
    key: string,
    value: 'None' | 'Read' | 'Write'
  ) => {
    const newPermissions = { ...permissions };
    
    if (section === 'main') {
      (newPermissions as any)[key] = value;
    } else if (section === 'settings') {
      newPermissions.Settings = {
        ...newPermissions.Settings,
        [key]: value as 'None' | 'Read' | 'Write',
      };
    } else if (section === 'actions') {
      newPermissions.Actions = {
        ...newPermissions.Actions,
        [key]: value as 'Read' | 'Write',
      };
    }
    
    setPermissions(newPermissions);
  };

  const handleToggle = (value: 'Default' | 'Custom') => {
    setIsDefault(value === 'Default');
    if (value === 'Default') {
      setPermissions(defaultPermissions);
    }
  };

  const handleSave = () => {
    onConfirm(permissions);
  };

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      title="Access"
      blurb={<>
        What <span className="font-courrier text-navy-100">{email}</span> can see
        and change in this app.
      </>}
      width="42rem"
      footer={<>
        <DsButton onClick={onClose} isDisabled={isSubmitting}>Cancel</DsButton>
        <DsButton variant="primary" onClick={handleSave} isDisabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save'}
        </DsButton>
      </>}
    >
      <div className="flex flex-col gap-3">
        {/* The Default/Custom switch was a hand-rolled sliding toggle. It is the
            same control the log tabs and the metric ranges use. */}
        <div className="flex items-center justify-between gap-3">
          <Eyebrow>features</Eyebrow>
          <SegmentedControl
            ariaLabel="Permission preset"
            segments={[
              { value: 'Default', label: 'Default' },
              { value: 'Custom', label: 'Custom' },
            ]}
            value={isDefault ? 'Default' : 'Custom'}
            onChange={(value) => handleToggle(value as 'Default' | 'Custom')}
          />
        </div>

        <div className="max-h-[46vh] overflow-y-auto b4-thin rounded-card border border-navy-500 bg-navy-700 divide-y divide-navy-500">
          <PermissionRow
            label="Default App Access"
            description="Read access to Overview, Logs, Deployments, Metrics and General Settings."
            currentValue="Read"
            isDefault={isDefault}
            readOnly={true}
            permissionType="readOnly"
            section="main"
            keyName="DefaultAppAccess"
            onPermissionChange={handlePermissionChange}
          />
          {settingsFeatures.map((feature) => (
            <PermissionRow
              key={feature.key}
              label={feature.label}
              description={feature.description}
              currentValue={permissions.Settings[feature.key as keyof ContainersPermissionSettings] as string}
              isDefault={isDefault}
              readOnly={feature.readOnly}
              permissionType={feature.readOnly ? 'readOnly' : 'noneReadWrite'}
              section="settings"
              keyName={feature.key}
              onPermissionChange={handlePermissionChange}
            />
          ))}
          <PermissionRow
            label="Deploy"
            description="Trigger deployments of this app."
            currentValue={permissions.Actions.Deploy}
            isDefault={isDefault}
            readOnly={false}
            permissionType="readWrite"
            section="actions"
            keyName="Deploy"
            onPermissionChange={handlePermissionChange}
          />
        </div>

        {error ? <span className="text-ds-115 text-state-err">{error}</span> : null}
      </div>
    </ModalDialog>
  );
};

export default CollaboratorPermissionModal;

